import React, { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppDispatch } from '../../../state/hooks';
import {
    demoModeEnabled,
    fetchIssueStatus,
    fetchVerifyStatus,
    issueFlowSelector,
    issuedCredentialsSelector,
    issueTimedOut,
    resetIssue,
    resetVerify,
    selectIssueCredential,
    selectVerifyProof,
    startIssuance,
    startVerification,
    verifyFlowSelector,
    verifyTimedOut,
} from '../../../state/slices/playground';
import {
    credencoWebsiteUrl,
    francePlaygroundUrl,
    issuableCredentials,
    verifiableProofs,
    walletDownload,
} from './playgroundConfig';
import { brand, brandFont, outlinePillButton, pillButton, radius, shadow } from './brand';
import { CredencoMark } from './CredencoMark';
import { LanguageMenu } from './LanguageMenu';
import { FloatingCards } from './FloatingCards';
import { QrPanel } from './QrPanel';
import { AttributeTable } from './AttributeTable';
import { HowItWorks } from './HowItWorks';
import { PhaseOverview, PhaseSection } from './PhaseSection';
import { ProgressTracker } from './ProgressTracker';
import { usePolling } from './usePolling';
import { jumpIntoView, scrollIntoView } from './smoothScroll';
import { credentialsNeededByUseCases, useCases } from './useCases';
import { UseCaseSection } from './useCases/UseCaseSection';

// Session ids only need to be unique per visitor, not unguessable: the backend
// correlates on them, it does not trust them. crypto.randomUUID() is unavailable
// in jsdom and outside secure contexts, so keep it plain.
const newSessionId = (): string => `itb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const riseIn = keyframes`
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const entrance = (delay: string) => ({
    animation: `${riseIn} .7s cubic-bezier(.2,.7,.3,1) ${delay} both`,
    '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
});

const greenArrow = <ArrowForwardIcon sx={{fontSize: 18, color: brand.green}}/>;

const ChoiceCard: FC<{title: string, description: string, done: boolean, onSelect: () => void,
        warning?: string, warningTestId?: string}> = (
        {title, description, done, onSelect, warning, warningTestId}) => {
    const {t} = useTranslation();
    return (
        <Box
            onClick={onSelect}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    onSelect();
                }
            }}
            sx={{
                background: brand.surface,
                border: `1px solid ${done ? brand.green : brand.border}`,
                borderRadius: radius.card,
                padding: '26px 24px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: shadow.card,
                transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    borderColor: done ? brand.green : brand.accentSoft,
                    boxShadow: '0 12px 28px rgba(3, 19, 61, .12)',
                },
                '&:focus-visible': {outline: `2px solid ${brand.blue}`, outlineOffset: '3px'},
                '@media (prefers-reduced-motion: reduce)': {transition: 'none', '&:hover': {transform: 'none'}},
            }}>
            <Typography sx={{color: brand.navy, fontWeight: 700, fontSize: '1.15rem'}}>{title}</Typography>
            <Typography sx={{color: brand.text, fontSize: '.95rem', lineHeight: 1.55}}>{description}</Typography>
            {warning && (
                <Stack
                    direction="row"
                    spacing={1}
                    data-testid={warningTestId}
                    sx={{
                        background: '#FFF8E8',
                        border: '1px solid #F0DCA8',
                        borderRadius: radius.card,
                        padding: '10px 12px',
                    }}>
                    <InfoOutlinedIcon sx={{color: '#8A6A12', fontSize: 18}}/>
                    <Typography sx={{color: '#6B520C', fontSize: '.85rem', lineHeight: 1.5}}>{warning}</Typography>
                </Stack>
            )}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{marginTop: 'auto', paddingTop: '14px'}}>
                {done && <CheckCircleIcon sx={{fontSize: 18, color: brand.blue}}/>}
                <Typography sx={{color: brand.navy, fontWeight: 700, fontSize: '.92rem'}}>
                    {done ? t('steps.issue.issued') : t('hero.cta')}
                </Typography>
                {!done && greenArrow}
            </Stack>
        </Box>
    );
};

const cardGrid = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'},
    gap: '18px',
};

export const PlaygroundPage: FC = () => {
    const dispatch = useAppDispatch();
    const {t} = useTranslation();
    const issue = useSelector(issueFlowSelector);
    const verify = useSelector(verifyFlowSelector);
    const issued = useSelector(issuedCredentialsSelector);

    usePolling(
            issue.status === 'pending' && issue.sessionId !== undefined,
            () => dispatch(fetchIssueStatus({sessionId: issue.sessionId!})),
            () => dispatch(issueTimedOut()),
    );

    usePolling(
            verify.status === 'pending' && verify.sessionId !== undefined,
            () => dispatch(fetchVerifyStatus({sessionId: verify.sessionId!, queryId: verify.selectedKey!})),
            () => dispatch(verifyTimedOut()),
    );

    const onIssue = (credentialType: string) => {
        const sessionId = newSessionId();
        dispatch(selectIssueCredential({selectedKey: credentialType, sessionId}));
        dispatch(startIssuance({sessionId, credentialType}));
    };

    const onVerify = (queryId: string) => {
        const sessionId = newSessionId();
        dispatch(selectVerifyProof({selectedKey: queryId, sessionId}));
        dispatch(startVerification({sessionId, queryId}));
    };

    // Bringing the fresh QR into view is the point where a visitor most often
    // loses the thread: the cards vanish and the code appears further down.
    const issuePanelRef = useRef<HTMLDivElement>(null);
    const verifyPanelRef = useRef<HTMLDivElement>(null);
    const issueQrRef = useRef<HTMLDivElement>(null);
    const verifyQrRef = useRef<HTMLDivElement>(null);

    // Twice per flow: on the click there is only a spinner to move to, and the QR
    // arrives a beat later with the wallet's answer. That second pass is the one
    // that puts the code itself in the middle of the screen.
    useEffect(() => {
        if (issue.status !== 'idle') {
            scrollIntoView(issueQrRef.current ?? issuePanelRef.current);
        }
    }, [issue.status === 'idle', issue.requestUri]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (verify.status !== 'idle') {
            scrollIntoView(verifyQrRef.current ?? verifyPanelRef.current);
        }
    }, [verify.status === 'idle', verify.requestUri]); // eslint-disable-line react-hooks/exhaustive-deps

    // Anchors are animated rather than left to the browser: `scroll-behavior:
    // smooth` cannot be slowed down, and a consistent pace across every jump on
    // the page is the whole point of the guidance.
    const onAnchorClick = (event: React.MouseEvent<HTMLElement>) => {
        const anchor = (event.target as HTMLElement).closest('a[href^="#"]');
        const hash = anchor?.getAttribute('href');
        const target = hash && document.querySelector(hash);
        if (!target) {
            return;
        }
        event.preventDefault();
        scrollIntoView(target, 'start');
        window.history.replaceState(null, '', hash);
    };

    // The marketplace listing links straight into a section (`/#issue-credentials`).
    // The browser cannot follow that on its own: nothing carries that id yet while
    // the document is parsed, so the jump has to wait for the first render.
    useEffect(() => {
        jumpIntoView(document.getElementById(window.location.hash.slice(1)));
    }, []);

    // No confirmation screen: the card turning green is the confirmation.
    const justIssued = useRef(false);

    useEffect(() => {
        if (issue.status === 'success') {
            justIssued.current = true;
            dispatch(resetIssue());
        }
    }, [issue.status, dispatch]);

    const offered = issuableCredentials.map(credential => credential.credentialType);
    const requiredByUseCases = credentialsNeededByUseCases(offered);

    // The ref keeps it to once: re-issuing something you already hold should not
    // drag the page down again.
    const complete = requiredByUseCases.length > 0
            && requiredByUseCases.every(credentialType => issued.includes(credentialType));
    const alreadyGuided = useRef(false);

    // The QR was centred, and it disappears the moment the wallet accepts. Without
    // moving back up, the visitor is left at the bottom of the grid with the card
    // that just turned green above the fold. Skipped once everything is issued,
    // because the effect below then moves on to the use cases.
    useEffect(() => {
        if (issue.status !== 'idle' || !justIssued.current) {
            return;
        }
        justIssued.current = false;
        if (!complete) {
            scrollIntoView(issuePanelRef.current, 'start');
        }
    }, [issue.status, complete]);

    useEffect(() => {
        if (!complete || alreadyGuided.current) {
            return;
        }
        alreadyGuided.current = true;
        // A beat after the choices are back, so the third card is seen turning green.
        const timer = window.setTimeout(() => scrollIntoView(document.getElementById('use-cases'), 'start'), 700);
        return () => window.clearTimeout(timer);
    }, [complete]);

    return (
        <Box onClick={onAnchorClick} sx={{fontFamily: brandFont, background: brand.background, minHeight: '100vh'}}>

            {demoModeEnabled() && (
                <Box sx={{
                    background: brand.green,
                    color: brand.navy,
                    textAlign: 'center',
                    padding: '9px 16px',
                    fontWeight: 700,
                    fontSize: '.88rem',
                }}>
                    {t('demoBanner')}
                </Box>
            )}

            <Container maxWidth="lg" sx={{paddingTop: {xs: '18px', md: '26px'}}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{color: brand.navy}}>
                        <CredencoMark size={26}/>
                        <Typography sx={{
                            fontWeight: 700,
                            fontSize: {xs: '1.15rem', md: '1.4rem'},
                            letterSpacing: '.02em',
                            color: brand.navy,
                        }}>
                            CREDENCO
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <LanguageMenu/>
                        <Button
                            href={credencoWebsiteUrl}
                            variant="contained"
                            endIcon={greenArrow}
                            sx={{
                                ...pillButton,
                                display: {xs: 'none', sm: 'inline-flex'},
                                background: brand.navy,
                                color: '#FFFFFF',
                                fontSize: '.92rem',
                                '&:hover': {background: brand.navy800, boxShadow: 'none'},
                            }}>
                            {t('nav.website')}
                        </Button>
                    </Stack>
                </Stack>
            </Container>

            <Container maxWidth="lg" sx={{paddingTop: {xs: '24px', md: '40px'}, paddingBottom: {xs: '8px', md: '16px'}}}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', md: '1.05fr .95fr'},
                    gap: {xs: '32px', md: '40px'},
                    alignItems: 'center',
                }}>
                    <Stack spacing={3} sx={{maxWidth: '34ch'}}>
                        <Box sx={{
                            ...entrance('0s'),
                            border: `1px solid ${brand.accentSoft}`,
                            color: brand.blue,
                            background: brand.surface,
                            borderRadius: radius.pill,
                            padding: '5px 15px',
                            alignSelf: 'flex-start',
                            fontSize: '.82rem',
                            fontWeight: 600,
                        }}>
                            {t('hero.badge')}
                        </Box>
                        <Typography variant="h1" sx={{
                            ...entrance('.08s'),
                            color: brand.navy,
                            fontWeight: 700,
                            fontSize: {xs: '2.1rem', md: '2.9rem'},
                            lineHeight: 1.05,
                            letterSpacing: '-.03em',
                        }}>
                            {t('hero.title')}
                            <Box component="span" sx={{color: brand.blue, display: 'block'}}>
                                {t('hero.titleAccent')}
                            </Box>
                        </Typography>
                        <Typography sx={{
                            ...entrance('.16s'),
                            color: brand.text,
                            fontSize: '1.06rem',
                            lineHeight: 1.6,
                        }}>
                            {t('hero.body')}
                        </Typography>
                        {/* useFlexGap, not the default: Stack spaces with margin-left,
                            which survives a line break and indents the wrapped button.
                            Real CSS gap keeps both flush with the column. */}
                        <Stack direction="row" spacing={1.5} useFlexGap sx={{...entrance('.24s'), flexWrap: 'wrap'}}>
                            <Button
                                href="#get-started"
                                variant="contained"
                                endIcon={greenArrow}
                                sx={{
                                    ...pillButton,
                                    background: brand.navy,
                                    color: '#FFFFFF',
                                    '&:hover': {background: brand.navy800, boxShadow: 'none'},
                                }}>
                                {t('hero.cta')}
                            </Button>
                            <Button
                                href="#see-a-presentation"
                                variant="outlined"
                                sx={outlinePillButton}>
                                {t('hero.ctaVerify')}
                            </Button>
                            <Button
                                href="#how-it-works"
                                variant="outlined"
                                sx={outlinePillButton}>
                                {t('hero.ctaSecondary')}
                            </Button>
                        </Stack>
                    </Stack>

                    <Box sx={{...entrance('.3s'), display: {xs: 'none', md: 'block'}}}>
                        <FloatingCards/>
                    </Box>
                </Box>
            </Container>

            <PhaseOverview items={[
                {step: 1, label: t('overview.wallet'), href: '#get-started'},
                {step: 2, label: t('overview.issue'), href: '#issue-credentials'},
                {step: 3, label: t('overview.rent'), href: '#use-cases'},
            ]}/>

            <PhaseSection
                id="how-it-works"
                tone="white"
                nextHref="#get-started"
                nextLabel={t('overview.next')}
                eyebrow={t('howItWorks.eyebrow')}
                title={t('howItWorks.title')}
                intro={t('howItWorks.intro')}>
                <HowItWorks/>
            </PhaseSection>

            <PhaseSection
                id="get-started"
                tone="light"
                step={1}
                nextHref="#issue-credentials"
                nextLabel={t('overview.next')}
                eyebrow={t('steps.wallet.eyebrow')}
                title={t('steps.wallet.title')}
                intro={t('steps.wallet.intro')}>
                <Stack spacing={3} alignItems="flex-start">
                    {/* Our wallet leads, because it is the one that can actually receive
                        these credentials. It only renders once it is downloadable: a
                        disabled button with an apology reads as a dead end. */}
                    {(walletDownload.appStoreUrl || walletDownload.playStoreUrl) ? (
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                            {walletDownload.appStoreUrl && (
                                <Button
                                    href={walletDownload.appStoreUrl}
                                    variant="contained"
                                    endIcon={greenArrow}
                                    data-testid="wallet-credenco-ios"
                                    sx={{
                                        ...pillButton,
                                        background: brand.navy,
                                        color: '#FFFFFF',
                                        '&:hover': {background: brand.navy800, boxShadow: 'none'},
                                    }}>
                                    {t('steps.wallet.ios')}
                                </Button>
                            )}
                            {walletDownload.playStoreUrl && (
                                <Button
                                    href={walletDownload.playStoreUrl}
                                    variant="outlined"
                                    endIcon={greenArrow}
                                    data-testid="wallet-credenco-android"
                                    sx={outlinePillButton}>
                                    {t('steps.wallet.android')}
                                </Button>
                            )}
                        </Stack>
                    ) : (
                        <Typography sx={{color: brand.text, fontSize: '.95rem'}}>
                            {t('steps.wallet.unavailable')}
                        </Typography>
                    )}
                </Stack>
            </PhaseSection>

            <PhaseSection
                id="issue-credentials"
                tone="white"
                step={2}
                nextHref="#use-cases"
                nextLabel={t('overview.next')}
                eyebrow={t('steps.issue.eyebrow')}
                title={t('steps.issue.title')}
                intro={t('steps.issue.intro')}>
                <Stack spacing={3} ref={issuePanelRef}>
                    <ProgressTracker required={requiredByUseCases} issued={issued}/>

                    {issue.status === 'idle' ? (
                        <Box sx={cardGrid}>
                            {issuableCredentials.map(credential => (
                                <ChoiceCard
                                    key={credential.credentialType}
                                    title={t(`credentials.${credential.credentialType}.title`)}
                                    description={t(`credentials.${credential.credentialType}.description`)}
                                    done={issued.includes(credential.credentialType)}
                                    onSelect={() => onIssue(credential.credentialType)}/>
                            ))}
                        </Box>
                    ) : (
                        <QrPanel
                            title={issue.selectedKey
                                ? t(`credentials.${issue.selectedKey}.title`)
                                : t('steps.issue.title')}
                            instruction={t('steps.issue.qrInstruction')}
                            status={issue.status}
                            requestUri={issue.requestUri}
                            qrRef={issueQrRef}
                            errorKey={issue.errorKey}
                            error={issue.error}
                            successMessage={t('steps.issue.success')}
                            onStartOver={() => dispatch(resetIssue())}/>
                    )}
                </Stack>
            </PhaseSection>

            <PhaseSection
                id="use-cases"
                tone="light"
                step={3}
                nextHref="#see-a-presentation"
                nextLabel={t('overview.next')}
                eyebrow={t('useCases.eyebrow')}
                title={t('useCases.sectionTitle')}
                intro={t('useCases.sectionIntro')}>
                <Stack spacing={4}>
                    {useCases.map(useCase => (
                        <UseCaseSection key={useCase.id} useCase={useCase} issued={issued}/>
                    ))}
                </Stack>
            </PhaseSection>

            <PhaseSection
                id="see-a-presentation"
                tone="muted"
                compact
                eyebrow={t('steps.verify.eyebrow')}
                title={t('steps.verify.title')}
                intro={t('steps.verify.intro')}>
                <Box ref={verifyPanelRef}>
                {verify.status === 'idle' ? (
                    <Box sx={cardGrid}>
                        {verifiableProofs.map(proof => {
                            const missing = proof.requiredCredentials.filter(c => !issued.includes(c));
                            return (
                                <ChoiceCard
                                    key={proof.queryId}
                                    title={t(`proofs.${proof.queryId}.title`)}
                                    description={t(`proofs.${proof.queryId}.description`)}
                                    done={false}
                                    warning={missing.length > 0 ? t('steps.verify.blockedWarning', {
                                        missing: missing.map(c => t(`credentials.${c}.title`)).join(', '),
                                    }) : undefined}
                                    warningTestId={`proof-warning-${proof.queryId}`}
                                    onSelect={() => onVerify(proof.queryId)}/>
                            );
                        })}
                    </Box>
                ) : (
                    <QrPanel
                        title={verify.selectedKey
                            ? t(`proofs.${verify.selectedKey}.title`)
                            : t('steps.verify.title')}
                        instruction={t('steps.verify.qrInstruction')}
                        status={verify.status}
                        requestUri={verify.requestUri}
                        qrRef={verifyQrRef}
                        errorKey={verify.errorKey}
                        error={verify.error}
                        successMessage={t('steps.verify.success')}
                        onStartOver={() => dispatch(resetVerify())}>
                        {verify.attributes && <AttributeTable attributes={verify.attributes}/>}
                    </QrPanel>
                )}
                </Box>
            </PhaseSection>

            <Box sx={{background: brand.navy, color: 'rgba(255,255,255,.72)', padding: '36px 0'}}>
                <Container maxWidth="lg">
                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{xs: 'flex-start', sm: 'center'}}>
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{color: '#FFFFFF'}}>
                            <CredencoMark size={20}/>
                            <Typography sx={{fontSize: '.9rem'}}>
                                {t('footer.tagline')}
                            </Typography>
                        </Stack>
                        <Typography
                            component="a"
                            href={francePlaygroundUrl}
                            sx={{fontSize: '.9rem', color: brand.accent, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}>
                            {t('footer.back')} &rarr;
                        </Typography>
                    </Stack>
                </Container>
            </Box>

        </Box>
    );
};
